import type { KeyboardEvent } from 'react';
import { CheckIcon, GiftIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { Drawer } from '../shared/Drawer';
import { IntervalChoice } from '../shared/IntervalChoice';
import { dayShort } from '../../core/format/dates';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { usageLabel } from '../../core/rules/usage';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { useSubscriptionBuilder, type BuilderTab } from './useSubscriptionBuilder';
const tabs: {
  key: BuilderTab;
  label: string;
}[] = [{
  key: 'protocol',
  label: 'Een protocol'
}, {
  key: 'products',
  label: 'Losse producten'
}];
const stepTitle = 'text-[13px] text-muted';
const chip = (on: boolean) => `whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'text-ink ring-1 ring-line hover:ring-ink/40'}`;

/**
 * "Stel je abonnement samen": een lade met drie stappen in één scherm. Wat je bijvult, hoe vaak,
 * en hoe de eerste leveringen eruitzien. De knop legt alles als bijvullen in de mand, met het
 * gekozen ritme; afrekenen gaat daarna gewoon via de mand.
 */
export function SubscriptionBuilder() {
  const b = useSubscriptionBuilder();
  const {
    routineTotals,
    productById
  } = useCatalog();
  // Tabbladen volgens de ARIA-afspraak: pijltjes wisselen, Tab gaat door naar de inhoud.
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const at = tabs.findIndex(t => t.key === b.tab);
    const nextIndex = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (at + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const next = tabs[nextIndex];
    if (!next) return;
    b.setTab(next.key);
    document.getElementById(`builder-tab-${next.key}`)?.focus();
  };
  const nameOf = (id: string) => productById(id)?.name ?? id;
  const footer = <div data-id="element-9155" data-mp="19jdgcaitxl5i">
      {(b.empty || b.tooSmall) && <p id="builder-empty" className="mb-3 text-center text-[13px] text-muted" data-id="element-9156" data-mp="19i2c69lh2bdv">
          {b.empty ? 'Kies eerst wat je wilt bijvullen.' : `Een doos is minstens ${euro(b.minBoxValue)}: voeg nog iets toe.`}
        </p>}
      <button type="button" onClick={b.submit} disabled={b.empty || b.tooSmall} aria-describedby={b.empty || b.tooSmall ? 'builder-empty' : undefined} className={btnPrice} data-id="element-9157" data-mp="csl1thbjo15u">
        <span className={btnPriceLabel} data-id="element-9158" data-mp="lsrifc2jhh98">In de mand</span>
        <span className={btnPriceAmount} data-id="element-9159" data-mp="lvdt3kkq8cvo">
          {euro(b.firstOrder)}
          <span className="text-[12px] opacity-80" data-id="element-9160" data-mp="225y7pazb1x92"> nu</span>
        </span>
      </button>
      <p className="mt-3 text-center text-[13px] text-muted" data-id="element-9161" data-mp="1x7mxv090fmqu">Zelfde prijs als los · {policyCopy.subscriptionPerks}</p>
    </div>;
  return <Drawer open={b.open} onClose={b.close} title="Stel je abonnement samen" footer={footer} data-id="element-9162" data-mp="25rt9fgzs3w88">
      <section aria-labelledby="builder-what" data-id="element-9163" data-mp="29opd1lewqdfn">
        <h3 id="builder-what" className={stepTitle} data-id="element-9164" data-mp="1vsatdbmzoulg">
          1 · Wat vul ik bij
        </h3>
        <div role="tablist" aria-label="Wat vul ik bij" className="mt-3 flex gap-1.5" onKeyDown={onTabKey} data-id="element-9165" data-mp="20pkh8muqcy76">
          {tabs.map(t => <button key={t.key} type="button" role="tab" id={`builder-tab-${t.key}`} aria-selected={b.tab === t.key} aria-controls={`builder-panel-${t.key}`} tabIndex={b.tab === t.key ? 0 : -1} onClick={() => b.setTab(t.key)} className={chip(b.tab === t.key)} data-id="element-9166" data-mp="2ogq3pkv6eoqg">
              {t.label}
            </button>)}
        </div>

        {b.tab === 'protocol' ? <ul id="builder-panel-protocol" role="tabpanel" aria-labelledby="builder-tab-protocol" className="mt-4 divide-y divide-line border-y border-line" data-id="element-9167" data-mp="3c7lv4gvpzhx6">
            {b.routineOptions.map(r => {
          const {
            items,
            price,
            months
          } = routineTotals(r);
          const on = b.routineId === r.id;
          return <li key={r.id} data-id="element-9168" data-mp="19kgfcdn0sa2u">
                  <button type="button"
            // Eén protocol tegelijk: een keuze, geen vinkje (schermlezer hoort "keuzerondje").
            role="radio" aria-checked={on} onClick={() => b.toggleRoutine(r.id)} className="flex w-full items-center gap-3 py-3 text-left" data-id="element-9169" data-mp="19l8ajncpg5sl">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? 'bg-ink text-shell' : 'ring-1 ring-line'}`} aria-hidden="true" data-id="element-9170" data-mp="1shzsntgigev2">
                      {on && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-9171" data-mp="2l76pmo0bgwe7" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-9172" data-mp="2sjeef605zlwe">
                      <span className="block text-[15px] leading-snug text-ink" data-id="element-9173" data-mp="2skpeovueibir">{r.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-9174" data-mp="2xjscpwbyrhh8">
                        {items.length} producten · gaat {months} mee
                      </span>
                    </span>
                    <span className="shrink-0 text-[14px] tabular-nums text-ink" data-id="element-9175" data-mp="37dsogh1r3kyx">{euro(price)}</span>
                  </button>
                </li>;
        })}
          </ul> : <div id="builder-panel-products" role="tabpanel" aria-labelledby="builder-tab-products" className="mt-4" data-id="element-9176" data-mp="3geh1p9jblhar">
            {b.chips.length > 1 && <div className="-mx-6 flex gap-1.5 overflow-x-auto px-6 pb-1" role="group" aria-label="Filter op huid" data-id="element-9177" data-mp="3q5bpwjg4mqyu">
                <button type="button" aria-pressed={b.skin === null} onClick={() => b.setSkin(null)} className={chip(b.skin === null)} data-id="element-9178" data-mp="3v71427ynbkr5">
                  Alles
                </button>
                {b.chips.map(s => <button key={s} type="button" aria-pressed={b.skin === s} onClick={() => b.setSkin(b.skin === s ? null : s)} className={chip(b.skin === s)} data-id="element-9179" data-mp="20076d7u2l62i">
                    {s}
                  </button>)}
              </div>}
            <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-9180" data-mp="242txt7mtwoz6">
              {b.productList.map(p => {
            const n = b.qty[p.id] ?? 0;
            return <li key={p.id} className="flex items-center gap-3 py-2.5" data-id="element-9181" data-mp="2dsmzngx65pq8">
                    <ProductImage src={p.image} alt="" width={40} className="h-12 w-10 shrink-0 bg-sand p-1" data-id="element-9182" data-mp="2rt7mqpa4a7pj" />
                    <div className="min-w-0 flex-1" data-id="element-9183" data-mp="2nrman65epxge">
                      {b.planIds.has(p.id) && <p className="text-[11px] text-clay" data-id="element-9184" data-mp="xf6ljxr3h8ma">Uit je huidplan</p>}
                      <p className="truncate text-[14px] leading-snug text-ink" data-id="element-9185" data-mp="xh9lpalyoi4q">{p.name}</p>
                      <p className="text-[12px] text-muted" data-id="element-9186" data-mp="tfxtqyfm7ktd">
                        {euro(p.price)}
                        {usageLabel(p) && ` · gaat ${usageLabel(p)} mee`}
                      </p>
                    </div>
                    {n === 0 ? <button type="button" onClick={() => b.setProductQty(p.id, 1)} aria-label={`${p.name} toevoegen`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink ring-1 ring-line transition-shadow duration-150 ease-soft hover:ring-ink/40" data-id="element-9187" data-mp="9j1xvdaefp7b">
                        <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-9188" data-mp="3mroeu3t61mun" />
                      </button> : <div className="flex shrink-0 items-center rounded-full bg-ink text-shell" data-id="element-9189" data-mp="3rrkjm59otib9">
                        <button type="button" onClick={() => b.setProductQty(p.id, n - 1)} aria-label={`Eén minder: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-9190" data-mp="1kxfcdrj9isao">
                          <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-9191" data-mp="2ykwicmlg4w2x" />
                        </button>
                        <span className="min-w-[1rem] text-center text-[13px] tabular-nums" aria-live="polite" data-id="element-9192" data-mp="2osqunp2890tj">
                          {n}
                        </span>
                        <button type="button" onClick={() => b.setProductQty(p.id, n + 1)} aria-label={`Eén meer: ${p.name}`} className="flex h-10 w-9 items-center justify-center" data-id="element-9193" data-mp="2kpkpylurjhp6">
                          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-9194" data-mp="2arwjwzrpinrp" />
                        </button>
                      </div>}
                  </li>;
          })}
            </ul>
          </div>}
        {b.count > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-9195" data-mp="41ynr0sgt6jc">
            Gekozen: {b.count} {b.count === 1 ? 'product' : 'producten'}
            {b.tab === 'products' && b.routineId && ' (met je protocol)'}
            {b.tab === 'protocol' && Object.values(b.qty).some(n => n > 0) && ' (met je losse producten)'}
          </p>}
      </section>

      <section aria-labelledby="builder-how" className="mt-9" data-id="element-9196" data-mp="1ro2uldq3si36">
        <h3 id="builder-how" className={stepTitle} data-id="element-9197" data-mp="1rqyq6vehauog">
          2 · Hoe vaak
        </h3>
        {b.intervals.length > 1 && <div className="mt-3" data-id="element-9198" data-mp="z0710hvtz6am">
            <IntervalChoice intervals={b.intervals} value={b.intervalWeeks} onChange={b.chooseInterval} labelledBy="builder-how" describedBy="builder-how-hint" data-id="element-9199" data-mp="z18l1butr34h" />
          </div>}
        <p id="builder-how-hint" className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-9200" data-mp="17zka39g1lsut">
          {b.empty ? 'Kies eerst wat je bijvult; dan reken ik het uit.' : b.tooSmall ? `Een doos is altijd minstens ${euro(b.minBoxValue)}, zodat verzending gratis blijft. Met alleen dit lukt dat niet: voeg nog een product toe.` : `Elke ${b.intervalWeeks} weken een doos met alleen wat bijna op is. Niet steeds alles: wat langer meegaat, komt minder vaak.`}{' '}
          Je krijgt vooraf een mail; overslaan of pauzeren kan altijd in Mijn omgeving.
        </p>
      </section>

      <section aria-labelledby="builder-preview" className="mt-9" data-id="element-9201" data-mp="rgdohwod88bx">
        <h3 id="builder-preview" className={stepTitle} data-id="element-9202" data-mp="wf830mfavaq8">
          3 · Zo ziet het eruit
        </h3>
        {b.boxes.length > 0 ? <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-9203" data-mp="3uy5ckzzds84u">
            <li className="flex items-baseline justify-between gap-3 py-3 text-[14px]" data-id="element-9204" data-mp="3rc89n9ajsw3">
              <span className="text-ink" data-id="element-9205" data-mp="8p4bhdfdz1fl">Nu · je eerste bestelling, alles</span>
              <span className="tabular-nums text-muted" data-id="element-9206" data-mp="cqpn6vg6p9o7">{euro(b.firstOrder)}</span>
            </li>
            {b.boxes.map((box, i) => <li key={box.date} className="py-3" data-id="element-9207" data-mp="1k3rcv6dzzvkf" data-mpi={i}>
                <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-9208" data-mp="1o64jry4hdzis">
                  <span className="text-ink" data-id="element-9209" data-mp="1o7z4pcfr5rmv">
                    Rond {dayShort(box.date)}
                    {b.giftAt === i + 1 && <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-clay" data-id="element-9210" data-mp="34mbp4vfu48w1">
                        <GiftIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-9211" data-mp="2zv21u1hrdhnb" /> met cadeau
                      </span>}
                  </span>
                  <span className="tabular-nums text-muted" data-id="element-9212" data-mp="2pxfdkoekkdqo">{euro(box.total)}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-9213" data-mp="2ky2vsi2k1wuy">
                  {box.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${nameOf(l.productId)}`).join(', ')}
                </p>
              </li>)}
          </ol> : <p className="mt-3 text-[13px] text-muted" data-id="element-9214" data-mp="2bzqthisbtamo">{b.empty ? 'Nog niets gekozen.' : 'Voeg nog iets toe, dan zie je hier je dozen.'}</p>}
        {b.range &&
      // Wijzigt bij elke keuze in stap 1 en 2: voorlezen, zonder de focus te verplaatsen.
      <p className="mt-2 text-[13px] text-muted" aria-live="polite" data-id="element-9215" data-mp="ta3fuegqv3ms">
            {b.range.min === b.range.max ? `${euro(b.range.min)} per doos` : `Meestal ${euro(b.range.min)} tot ${euro(b.range.max)} per doos`}, altijd gratis verzending.
          </p>}
        {b.boxes.length > 0 && <p className="mt-2 text-[12px] leading-relaxed text-muted" data-id="element-9216" data-mp="pam8ffhrbwez">
            De datums zijn een schatting op basis van hoe lang een product gemiddeld meegaat. Vóór elke doos krijg je een mail met de echte datum en inhoud.
            {b.unknownNames.length > 0 && ` Van ${b.unknownNames.join(', ')} weten we nog niet precies hoe lang het meegaat; ${b.unknownNames.length === 1 ? 'dat gaat' : 'die gaan'} voorlopig elke doos mee.`}
          </p>}
      </section>
    </Drawer>;
}